import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { useLanguage } from '../../../../contexts/LanguageContext';
import SellableActionDialog from './SellableActionDialog';
import CreateRefundDialog from './CreateRefundDialog';
import {
    getStockStatusSeverity,
    getSellableSeverity,
    VEHICLE_STOCK_STATUS,
    VEHICLE_STOCK_SELLABLE
} from '../../vehiclesForSale/utils/statusBadges';

const VehicleStockTable = ({ data = [], loading = false, onRefetch }) => {
    const { th, getCurrentLocale } = useLanguage();

    const [selectedRow, setSelectedRow] = useState(null);
    const [refundRow, setRefundRow] = useState(null);

    const formatDate = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleDateString(getCurrentLocale(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return value;
        }
    };

    const handleOpen = (row) => setSelectedRow(row);
    const handleClose = () => setSelectedRow(null);
    const handleActionComplete = () => {
        onRefetch?.();
    };

    const actionBody = (row) => {
        if (row.status !== VEHICLE_STOCK_STATUS.IN_STOCK) {
            return <span className="text-500">—</span>;
        }
        return (
            <div className="flex align-items-center gap-2">
                <Button
                    icon="pi pi-tag"
                    rounded
                    text
                    severity="info"
                    tooltip={th('vehicleSalesMyVehicleStock.buttons.updateSellable')}
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleOpen(row)}
                />
                <Button
                    icon="pi pi-undo"
                    rounded
                    text
                    severity="warning"
                    tooltip={th('vehicleSalesMyVehicleStock.buttons.startRefund')}
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => setRefundRow(row)}
                    disabled={row.sellable === VEHICLE_STOCK_SELLABLE.FOR_SALE}
                />
                {row.hasActiveSaleListing && (
                    <Tag
                        value={th('vehicleSalesMyVehicleStock.badge.activeListing')}
                        severity="success"
                        icon="pi pi-shopping-cart"
                    />
                )}
            </div>
        );
    };

    const statusBody = (row) => {
        if (!row.statusText) return '-';
        return (
            <Tag
                value={row.statusText}
                severity={getStockStatusSeverity(row.status)}
            />
        );
    };

    const sellableBody = (row) => {
        if (row.sellableText == null) return '-';
        return (
            <div className="flex align-items-center gap-2">
                <Tag
                    value={row.sellableText}
                    severity={getSellableSeverity(row.sellable)}
                />
                {row.pendingRequestCount > 0 && (
                    <Badge
                        value={row.pendingRequestCount}
                        severity="warning"
                        title={th('vehicleSalesMyVehicleStock.badge.pendingRequests')}
                    />
                )}
            </div>
        );
    };

    return (
        <>
            <DataTable
                value={data}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                loading={loading}
                showGridlines
                stripedRows
                responsiveLayout="scroll"
                emptyMessage={th('common.noRecords')}
                dataKey="id"
                className="p-datatable-gridlines"
            >
                <Column
                    header={th('vehicleSalesMyVehicleStock.columns.actions')}
                    body={actionBody}
                    style={{ minWidth: '180px', width: '180px' }}
                    frozen
                />
                <Column field="chassisNo" header={th('vehicleSalesMyVehicleStock.columns.chassisNo')} sortable style={{ minWidth: '150px' }} />
                <Column field="brand" header={th('vehicleSalesMyVehicleStock.columns.brand')} sortable style={{ minWidth: '100px' }} />
                <Column field="model" header={th('vehicleSalesMyVehicleStock.columns.model')} sortable style={{ minWidth: '100px' }} />
                <Column field="matnr" header={th('vehicleSalesMyVehicleStock.columns.matnr')} sortable style={{ minWidth: '120px' }} />
                <Column field="maktx" header={th('vehicleSalesMyVehicleStock.columns.maktx')} sortable style={{ minWidth: '180px' }} />
                <Column
                    field="statusText"
                    header={th('vehicleSalesMyVehicleStock.columns.status')}
                    body={statusBody}
                    sortable
                    style={{ minWidth: '120px' }}
                />
                <Column
                    field="sellableText"
                    header={th('vehicleSalesMyVehicleStock.columns.sellable')}
                    body={sellableBody}
                    sortable
                    style={{ minWidth: '160px' }}
                />
                <Column
                    field="createdDate"
                    header={th('vehicleSalesMyVehicleStock.columns.createdDate')}
                    body={(row) => formatDate(row.createdDate)}
                    sortable
                    style={{ minWidth: '160px' }}
                />
            </DataTable>

            <SellableActionDialog
                visible={!!selectedRow}
                row={selectedRow}
                onHide={handleClose}
                onActionComplete={handleActionComplete}
            />

            <CreateRefundDialog
                visible={!!refundRow}
                row={refundRow}
                onHide={() => setRefundRow(null)}
                onActionComplete={handleActionComplete}
            />
        </>
    );
};

export default VehicleStockTable;
